/**
 * Turns raw imported text (from a PDF or Markdown file) into a set of proposed
 * Library notes. The AI path segments the source into coherent topics, distills
 * each into an ORIGINAL study note (never verbatim), and classifies it into one
 * of the user's disciplines — which is what lets the Library auto-organize on
 * import. An offline fallback splits Markdown by headings with no classification.
 */

import type { AIProvider } from "@/core/ai/types";
import { getCategories } from "@/store/customContentStore";

export interface ProposedNote {
  title: string;
  /** A discipline id, or "" when unclassified (offline import). */
  disciplineId: string;
  summary: string;
  tags: string[];
  /** Distilled Markdown body. */
  body: string;
}

/** Chunk large text at paragraph boundaries so each AI call stays bounded. */
function chunkText(text: string, limit = 14000, single = 24000): string[] {
  if (text.length <= single) return [text];
  const chunks: string[] = [];
  let cur = "";
  for (const p of text.split(/\n\n+/)) {
    if (cur && (cur.length + p.length + 2) > limit) {
      chunks.push(cur);
      cur = p;
    } else {
      cur = cur ? `${cur}\n\n${p}` : p;
    }
  }
  if (cur.trim()) chunks.push(cur);
  return chunks;
}

function stripFences(s: string): string {
  return s.replace(/^\s*```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
}

/** AI pipeline: distilled, classified notes ready to file into the Library. */
export async function proposeNotesFromText(
  provider: AIProvider,
  rawText: string,
  sourceName: string,
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal
): Promise<ProposedNote[]> {
  const cats = getCategories();
  const idSet = new Set(cats.map((c) => c.id));
  const catList = cats.map((c) => `- ${c.id}: ${c.name} (${c.group})`).join("\n");
  const chunks = chunkText(rawText);
  const out: ProposedNote[] = [];

  const system =
    "You are a game-design librarian building a personal study library. You distill source material into concise, ORIGINAL study notes — never copy sentences verbatim; rewrite everything in your own words. You classify each note into exactly one discipline from the provided list. You output STRICT JSON only, with no prose before or after.";

  for (let i = 0; i < chunks.length; i++) {
    onProgress?.(i, chunks.length);
    const user = `Disciplines (use the id string exactly):
${catList}

From the SOURCE text below (from "${sourceName}"), produce study notes. Split it into coherent topics (roughly one per meaningful subject). For each topic output an object:
{"title": a short title,
 "disciplineId": the one id from the list above that best fits,
 "summary": one sentence,
 "tags": 2-4 short kebab-case tags,
 "body": a Markdown study note IN YOUR OWN WORDS (about 120-250 words; use ## sub-headings and bullet points; do NOT copy sentences from the source)}

Return ONLY a JSON array of such objects.

SOURCE:
${chunks[i]}`;

    const text = await provider.ask(system, user, 4000, signal);
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(stripFences(text));
    } catch {
      parsed = null;
    }
    if (Array.isArray(parsed)) {
      for (const raw of parsed) {
        const o = raw as Record<string, unknown>;
        if (!o || typeof o.title !== "string" || typeof o.body !== "string") continue;
        const disciplineId = typeof o.disciplineId === "string" && idSet.has(o.disciplineId) ? o.disciplineId : "";
        out.push({
          title: (o.title as string).trim().slice(0, 120) || "Untitled note",
          disciplineId,
          summary: typeof o.summary === "string" ? (o.summary as string).trim() : "",
          tags: Array.isArray(o.tags) ? o.tags.map((t) => String(t).trim()).filter(Boolean).slice(0, 6) : [],
          body: (o.body as string).trim(),
        });
      }
    }
  }

  onProgress?.(chunks.length, chunks.length);
  return out;
}

/** Offline structural split of Markdown by headings (no AI, no classification). */
export function splitMarkdown(md: string): ProposedNote[] {
  const notes: ProposedNote[] = [];
  let title = "";
  let buf: string[] = [];
  const flush = () => {
    const body = buf.join("\n").trim();
    if (body) notes.push({ title: title || "Section", disciplineId: "", summary: "", tags: [], body });
    buf = [];
  };
  for (const line of md.split("\n")) {
    const m = line.match(/^#{1,3}\s+(.+)$/);
    if (m) {
      flush();
      title = m[1].trim();
    }
    buf.push(line);
  }
  flush();
  return notes.length ? notes : [{ title: "Imported note", disciplineId: "", summary: "", tags: [], body: md.trim() }];
}
